import { Component, EventEmitter, Inject, Input, OnInit, Output } from "@angular/core";

import * as site from "site";
import { AircraftSubscriptionTrial } from "../aircraft-subscription-trial";
import { AircraftSubscriptionTrialsService } from "../aircraft-subscription-trials.service";
import { AircraftService } from "../aircraft.service";
import { Aircraft } from "../aircraft";
import { ConnectedCockpitOemAvdbTrialRequest } from "../connected-cockpit-oem-avdb-trial-request";
import { ConnectedCockpitOemAvdbTrialResponse } from "../connected-cockpit-oem-avdb-trial-response";
import { ConnextAccount } from "../connext-account";
import { ConnextAddressToken } from "../connext-address-token";
import { Device } from "../device";
import { OemService } from "../oem.service";

enum State {
	Loading,
	RetrievingClaimedTrials,
	ClaimedTrialsRequestFailed,
	SubscriptionAlreadyClaimed,
	SubscriptionInfo,
	ActivatingSubscription,
	SubscriptionActivationRequestFailed,
	SubscriptionActivationFailedWithReason,
	SubscriptionActivationSucceeded
}

@Component({
	selector: "activate-ccd-oem-avdb-subscription-step",
	template: `
		<modal
			(close)="onModalClosed()"
			modalClasses="modal-wide"
			[clickToClose]="false">
			<header class="modal_header">
				<h2 class="text-center">
					Activate OEM Aviation Database Trial
					<br>
					<small style="font-size: 12px; font-weight: 400;">
						Step 4 of 4
					</small>
				</h2>
			</header>
			<div class="modal_body px-4 pt-4">
				<div
					*ngIf="currentState === State.Loading || currentState === State.RetrievingClaimedTrials"
					class="modal_content_box px-2">
					<div style="text-align: center;">
						<div class="loading-indicator">Loading...</div>
					</div>
				</div>
				<div
					*ngIf="currentState === State.ClaimedTrialsRequestFailed"
					class="modal_content_box px-2">
					<div style="text-align: center;">
						<div>
							<i class="fa fa-minus-circle fa-3x statuscolor-red" aria-hidden="true"></i>
						</div>
						<div class="my-2">
							We were unable to load aviation database<br>
							trials because of an error.
						</div>
						<div>
							<button
								(click)="retryClaimedTrialsRequest()"
								class="button button-secondary py-1 px-5">
								RETRY
							</button>
						</div>
					</div>
				</div>
				<div
					*ngIf="currentState === State.SubscriptionAlreadyClaimed"
					class="modal_content_box px-2">
					<div style="text-align: center;">
						<div>
							<strong>
								This aircraft has already claimed<br>
								an OEM aviation database trial.
							</strong>
						</div>
						<div class="mt-2">
							Please contact Aviation Product Support if you<br>
							need additional OEM aviation database trials.
						</div>
					</div>
				</div>
				<div
					*ngIf="currentState === State.SubscriptionInfo"
					class="modal_content_box px-2">
					<div style="text-align: center;">
						<div>
							Aviation Database Trial:
							<br>
							<strong>
								Worldwide OEM Test Standard + FliteCharts PilotPak
							</strong>
						</div>
						<div class="my-2">
							This trial will provide two issues of all<br>
							aviation databases for OEM testing purposes.
						</div>
						<div>
							<button
								(click)="activateAvdbTrial()"
								class="button button-primary py-1 px-5">
								START AVIATION DATABASE TRIAL
							</button>
						</div>
					</div>
				</div>
				<div
					*ngIf="currentState === State.ActivatingSubscription"
					class="modal_content_box px-2">
					<div style="text-align: center;">
						<div class="loading-indicator">Activating database trial...</div>
					</div>
				</div>
				<div
					*ngIf="currentState === State.SubscriptionActivationRequestFailed"
					class="modal_content_box px-2">
					<div style="text-align: center;">
						<div>
							<i class="fa fa-minus-circle fa-3x statuscolor-red" aria-hidden="true"></i>
						</div>
						<div class="my-2">
							We were unable to activate this trial<br>
							because of an error.
						</div>
						<div>
							<button
								(click)="activateAvdbTrial()"
								class="button button-secondary py-1 px-5">
								RETRY
							</button>
						</div>
					</div>
				</div>
				<div
					*ngIf="currentState === State.SubscriptionActivationFailedWithReason"
					class="modal_content_box px-2">
					<div style="text-align: center;">
						<div>
							<i class="fa fa-minus-circle fa-3x statuscolor-red" aria-hidden="true"></i>
						</div>
						<div class="my-2">
							We were unable to activate this trial<br>
							because of the following issue:
						</div>
						<div class="my-2">
							<small>
								<em>
									{{errorMessage}}
								</em>
							</small>
						</div>
						<div>
							<button
								(click)="activateAvdbTrial()"
								class="button button-secondary py-1 px-5">
								RETRY
							</button>
						</div>
					</div>
				</div>
				<div
					*ngIf="currentState === State.SubscriptionActivationSucceeded"
					class="modal_content_box px-2">
					<div style="text-align: center;">
						<div>
							<i class="fa fa-check-circle fa-3x statuscolor-green" aria-hidden="true"></i>
						</div>
						<div class="my-2">
							Aviation Database Trial Activated:
							<br>
							<strong>
								Worldwide OEM Test Standard + FliteCharts PilotPak
							</strong>
						</div>
						<div class="mt-2">
							Click the Redeem button below to redeem the PilotPak for this aircraft.
						</div>
					</div>
				</div>
				<aviation-product-support-contact-details></aviation-product-support-contact-details>
			</div>
			<footer class="modal_footer">
				<div>
					<button
						(click)="onModalClosed()"
						class="button button-secondary py-1 px-4">
						CANCEL
					</button>
					<button
						(click)="continueButtonClicked()"
						[disabled]="shouldDisableContinueButton()"
						class="button {{getContinueButtonClass()}} py-1 px-4 modal_buttons">
						{{getContinueButtonText()}}
					</button>
				</div>
			</footer>
		</modal>
	`,
})
export class ActivateCCDOemAvdbSubscriptionStepComponent implements OnInit {
	@Input() masterAircraftUuid: string;
	@Input() selectedBillingAccount: ConnextAccount;
	@Input() selectedConnextAddressToken: ConnextAddressToken;
	@Output() oemAvdbTrialSubscribed = new EventEmitter<string>();
	@Output() modalClosed = new EventEmitter();
	readonly State = State;
	currentState: State = State.Loading;
	v2OemAvdbTrialType: number;
	ccdOemAvdbTestBundlePartKey: string;
	ccdOemAvdbTestBundlePartKeyTier2: string;
	ccdOemAvdbTestBundlePartKeyTier3: string;
	claimedTrials: AircraftSubscriptionTrial[] = [];
	errorMessage: string;

	constructor(
		private oemService: OemService,
		private trialsService: AircraftSubscriptionTrialsService,
		private aircraftService: AircraftService,
		@Inject("v2OemAvdbTrialType") v2OemAvdbTrialType: number,
		@Inject("ccdOemAvdbTestBundlePartKey") ccdOemAvdbTestBundlePartKey: string,
		@Inject("ccdOemAvdbTestBundlePartKeyTier2") ccdOemAvdbTestBundlePartKeyTier2: string,
		@Inject("ccdOemAvdbTestBundlePartKeyTier3") ccdOemAvdbTestBundlePartKeyTier3: string,
	) {
		this.v2OemAvdbTrialType = v2OemAvdbTrialType;
		this.ccdOemAvdbTestBundlePartKey = ccdOemAvdbTestBundlePartKey;
		this.ccdOemAvdbTestBundlePartKeyTier2 = ccdOemAvdbTestBundlePartKeyTier2;
		this.ccdOemAvdbTestBundlePartKeyTier3 = ccdOemAvdbTestBundlePartKeyTier3;
	}

	ngOnInit() {
		this.loadClaimedTrials();
	}

	onModalClosed() {
		this.modalClosed.emit();
	}

	continueButtonClicked() {
		if (this.currentState === State.SubscriptionActivationSucceeded) {
			this.navigateToRedeemPilotPak();
		} else {
			this.oemAvdbTrialSubscribed.emit();
		}
	}

	shouldDisableContinueButton(): boolean {
		switch (this.currentState) {
			case State.ClaimedTrialsRequestFailed:
			case State.SubscriptionAlreadyClaimed:
			case State.SubscriptionInfo:
			case State.SubscriptionActivationRequestFailed:
			case State.SubscriptionActivationFailedWithReason:
			case State.SubscriptionActivationSucceeded:
				return false;
			case State.Loading:
			case State.RetrievingClaimedTrials:
			case State.ActivatingSubscription:
			default:
				return true;
		}
	}

	getContinueButtonClass(): string {
		if (this.currentState === State.SubscriptionActivationSucceeded) {
			return "button-primary";
		} else {
			return "button-secondary";
		}
	}

	getContinueButtonText(): string {
		switch (this.currentState) {
			case State.ClaimedTrialsRequestFailed:
			case State.SubscriptionAlreadyClaimed:
			case State.SubscriptionInfo:
			case State.SubscriptionActivationRequestFailed:
			case State.SubscriptionActivationFailedWithReason:
				return "Skip This Trial";
			case State.SubscriptionActivationSucceeded:
				return "Redeem PilotPak";
			case State.Loading:
			case State.RetrievingClaimedTrials:
			case State.ActivatingSubscription:
			default:
				return "Close";
		}
	}

	retryClaimedTrialsRequest() {
		this.loadClaimedTrials();
	}

	activateAvdbTrial() {
		this.activateSubscription();
	}

	private loadClaimedTrials() {
		this.currentState = State.RetrievingClaimedTrials;
		this.claimedTrials = [];
		this.trialsService
			.getAircraftSubscriptionTrialsByMasterAircraftUuid(this.masterAircraftUuid)
			.subscribe((claimedTrials: AircraftSubscriptionTrial[]) => {
				this.claimedTrials = claimedTrials;
				const alreadyClaimed = this.claimedTrials.some((claimedTrial: AircraftSubscriptionTrial) => {
					return claimedTrial.masterAircraftUuid === this.masterAircraftUuid &&
						claimedTrial.trialTypeId === this.v2OemAvdbTrialType;
				});
				this.currentState = alreadyClaimed ? State.SubscriptionAlreadyClaimed : State.SubscriptionInfo;
			}, (error) => {
				console.error(error);
				this.currentState = State.ClaimedTrialsRequestFailed;
			});
	}

	private activateSubscription() {
		this.currentState = State.ActivatingSubscription;

		const request = new ConnectedCockpitOemAvdbTrialRequest({
			masterAircraftUuid: this.masterAircraftUuid
		});

		this.oemService.activateAvdbTrial(request).subscribe((response: ConnectedCockpitOemAvdbTrialResponse) => {
			if (response == null) {
				this.currentState = State.SubscriptionActivationRequestFailed;
			} else if (response.success) {
				this.currentState = State.SubscriptionActivationSucceeded;
			} else {
				this.errorMessage = response.message;
				this.currentState = State.SubscriptionActivationFailedWithReason;
			}
		}, (error) => {
			console.error(error);
			this.currentState = State.SubscriptionActivationRequestFailed;
		});
	}

	private navigateToRedeemPilotPak() {
		this.aircraftService.getAircraft(false, false, false).subscribe((aircraft: Aircraft[]) => {
			if (aircraft == null) {
				this.oemAvdbTrialSubscribed.emit();
			} else {
				const ac: Aircraft = aircraft.find((aircraft) => aircraft.masterAircraftUuid === this.masterAircraftUuid);
				if (ac == null) {
					this.oemAvdbTrialSubscribed.emit();
				} else {
					const device: Device = ac.devices.find((device) => device.model.isConnectedCockpitCapable)
					if (device == null) {
						this.oemAvdbTrialSubscribed.emit();
					} else {
						window.location.href = `${site.contextURL}/devices/details/${device.id}/redeem;partKey=${this.getAvdbPartKeyForAircraft(ac)}`;
					}
				}
			}
		}, (error) => {
			console.error(error);
			this.oemAvdbTrialSubscribed.emit();
		});
	}

	private getAvdbPartKeyForAircraft(aircraft: Aircraft): string {
		switch (aircraft.jeppTierId) {
			case 1: return this.ccdOemAvdbTestBundlePartKey;
			case 2: return this.ccdOemAvdbTestBundlePartKeyTier2;
			case 3: return this.ccdOemAvdbTestBundlePartKeyTier3;
			default:
				return this.ccdOemAvdbTestBundlePartKey;
		}
	}
}
